import { useCallback, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { apiSlice } from './apiSlice';
import type {
  SimSession,
  SimSessionWithAdvance,
  AdvanceDaysPayload,
  ResetSessionPayload,
  WeatherSeries,
  WeatherMapItem,
  CreateWeatherSeriesPayload,
  WeatherDataPoint,
  WeatherImportLocationsResponse,
  WeatherImportRequest,
  WeatherImportResult,
  WeatherExtendRequest,
  WeatherExtendResult,
  WeatherYearPolicy,
  WeatherYearPolicyMode,
  WeatherYearSelection,
  WeatherYearAssignment,
  CloneWeatherSeriesPayload,
  AddWeatherDataPointsPayload,
  GrowthProfile,
  ClimateNorm,
  ClimateNormDaily,
  SimulationTimeseriesResponse,
  SimulationSessionWeatherResponse,
  ApplyRuntimeReplayPayload,
  RuntimeReplayApplyResponse,
  SimEvent,
  CreateSimEventPayload,
  DayOverview,
  EntityTimeseriesType,
  EntityTimeseriesResponse,
  SimulationTimeseriesEntry,
  RealDayLogEntry,
  RealDayResult,
  GreenhouseClimateListResponse,
  GreenhouseClimatePeriod,
  GreenhouseClimateSnapshot,
  GreenhouseClimateTimeseriesResponse,
  UpsertGreenhouseClimatePeriodPayload,
} from '../types/simulation';
import type { SaveWorld } from '../types/saves';
import { useRefreshWorldOptional, useWorldOptional } from '../contexts/WorldContext';

type SaveRuntimeSessionQueryArg = string | typeof skipToken;

interface SaveRuntimeSessionQueryOptions {
  skip?: boolean;
  pollingInterval?: number;
}

const optionalRevision = (value: unknown): number | undefined => {
  const revision = Number(value);
  return Number.isFinite(revision) && revision >= 1 ? revision : undefined;
};

const buildSaveRuntimeSession = (world: SaveWorld | null | undefined): SimSession | null => {
  if (!world || world.type !== 'free') return null;
  const runtimeConfig = world.runtime?.config || {};
  const simSessionId = runtimeConfig.simSessionId || world.saveId;
  const inputRevision = optionalRevision(runtimeConfig.inputRevision);
  const materializedInputRevision = optionalRevision(runtimeConfig.materializedInputRevision) ?? inputRevision;
  const sessionConfig = {
    schemaVersion: runtimeConfig.schemaVersion,
    weatherControls: runtimeConfig.weatherControls,
    careStrategy: runtimeConfig.careStrategy,
    environmentOverrides: runtimeConfig.environmentOverrides,
    greenhouse: runtimeConfig.greenhouse,
  } as SimSession['config'];
  return {
    id: simSessionId,
    farmId: world.farm.id || '',
    saveId: world.saveId,
    status: world.status === 'active' ? 'active' : 'completed',
    isDefault: true,
    simDate: world.cursor?.simDate || '',
    advanceIntervalMs: 0,
    config: sessionConfig,
    weatherSeriesId: runtimeConfig.weatherSeriesId || null,
    inputRevision,
    materializedInputRevision,
    replayStatus: String(runtimeConfig.replayStatus || 'idle'),
    createdBy: null,
    createdAt: '',
    finishedAt: world.runtime?.terminal ? world.cursor?.simDate || null : null,
  };
};

const simulationApi = apiSlice
  .enhanceEndpoints({ addTagTypes: ['SimSession', 'SimOverview', 'WeatherSeries', 'GrowthProfile', 'ClimateNorm', 'ClimateNormDaily', 'SimEvent', 'SimTimeseries', 'GreenhouseClimate', 'SaveV2'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getSimSession: builder.query<SimSession, string>({
        query: (id) => `/simulation/sessions/${id}`,
        providesTags: (_r, _e, id) => [{ type: 'SimSession', id }],
      }),

      getActiveSimSession: builder.query<SimSession | null, string>({
        query: (farmId) => `/simulation/sessions/active?farmId=${farmId}`,
        providesTags: (result, _e, farmId) => [
          { type: 'SimSession', id: `active-${farmId}` },
          ...(result?.id ? [{ type: 'SimSession' as const, id: result.id }] : []),
        ],
      }),

      listSimSessions: builder.query<SimSession[], { farmId: string; status?: string }>({
        query: ({ farmId, status }) => {
          const params = new URLSearchParams({ farmId: String(farmId) });
          if (status) params.set('status', status);
          return `/simulation/sessions?${params}`;
        },
        providesTags: ['SimSession'],
      }),

      cancelSimSession: builder.mutation<{ message: string }, string>({
        query: (id) => ({ url: `/simulation/sessions/${id}/cancel`, method: 'POST' }),
        extraOptions: { skipHalt: true },
        invalidatesTags: ['SimSession'],
      }),

      pauseSimSession: builder.mutation<SimSession, string>({
        query: (id) => ({ url: `/simulation/sessions/${id}/pause`, method: 'POST' }),
        invalidatesTags: (_r, _e, id) => [{ type: 'SimSession', id }],
      }),

      resumeSimSession: builder.mutation<SimSession, string>({
        query: (id) => ({ url: `/simulation/sessions/${id}/resume`, method: 'POST' }),
        invalidatesTags: (_r, _e, id) => [{ type: 'SimSession', id }],
      }),

      advanceSimDays: builder.mutation<SimSessionWithAdvance, AdvanceDaysPayload>({
        query: ({ id, days, targetDate }) => ({
          url: `/simulation/sessions/${id}/advance`,
          method: 'POST',
          body: targetDate ? { targetDate } : { days },
        }),
        invalidatesTags: (result, _e, { id }) => [
          { type: 'SimSession', id },
          ...(result?.farmId ? [{ type: 'SimSession' as const, id: `active-${result.farmId}` }] : []),
          ...(result?.saveId ? [
            { type: 'SaveV2' as const, id: result.saveId },
            { type: 'SaveV2' as const, id: 'LIST' },
          ] : []),
          { type: 'SimOverview', id },
          { type: 'SimTimeseries', id: `session:${id}` },
          'Planting' as const,
          'PlantingAttribute' as any,
          'Plan' as const,
        ],
      }),

      advanceSaveSimDays: builder.mutation<SimSessionWithAdvance, { saveId: string; days?: number; targetDate?: string }>({
        query: ({ saveId, days, targetDate }) => ({
          url: `/saves/${saveId}/simulation/advance`,
          method: 'POST',
          body: targetDate ? { targetDate } : { days },
        }),
        invalidatesTags: (result, _e, { saveId }) => [
          { type: 'SaveV2', id: saveId },
          { type: 'SaveV2', id: 'LIST' },
          { type: 'SimSession', id: `save-${saveId}` },
          ...(result?.id ? [{ type: 'SimSession' as const, id: result.id }] : []),
          ...(result?.farmId ? [{ type: 'SimSession' as const, id: `active-${result.farmId}` }] : []),
          ...(result?.id ? [{ type: 'SimOverview' as const, id: result.id }] : []),
          ...(result?.id ? [{ type: 'SimTimeseries' as const, id: `session:${result.id}` }] : []),
          'Planting' as const,
          'PlantingAttribute' as any,
          'Plan' as const,
        ],
      }),

      resetSimSession: builder.mutation<SimSession, ResetSessionPayload>({
        query: ({ id, targetDate }) => ({
          url: `/simulation/sessions/${id}/reset`,
          method: 'POST',
          body: { targetDate },
        }),
        invalidatesTags: (result, _e, { id }) => {
          const saveId = (result as any)?.saveId || (result as any)?.session?.saveId;
          return [
            { type: 'SimSession' as const, id },
            ...(saveId ? [
              { type: 'SaveV2' as const, id: saveId },
              { type: 'SaveV2' as const, id: 'LIST' },
            ] : []),
            { type: 'SimOverview' as const, id },
            { type: 'SimTimeseries' as const, id: `session:${id}` },
            'Planting' as const,
            'PlantingAttribute' as any,
          ];
        },
      }),

      resetSaveSimSession: builder.mutation<
        { session?: SimSession; saveId?: string; rollback?: unknown },
        { saveId: string; targetDate: string }
      >({
        query: ({ saveId, targetDate }) => ({
          url: `/saves/${saveId}/simulation/reset`,
          method: 'POST',
          body: { targetDate },
        }),
        invalidatesTags: (result, _e, { saveId }) => [
          { type: 'SaveV2', id: saveId },
          { type: 'SaveV2', id: 'LIST' },
          { type: 'SimSession', id: `save-${saveId}` },
          ...(result?.session?.id ? [{ type: 'SimSession' as const, id: result.session.id }] : []),
          ...(result?.session?.id ? [{ type: 'SimOverview' as const, id: result.session.id }] : []),
          ...(result?.session?.id ? [{ type: 'SimTimeseries' as const, id: `session:${result.session.id}` }] : []),
          'Planting' as const,
          'PlantingAttribute' as any,
          'Plan' as const,
        ],
      }),

      pauseSaveSimSession: builder.mutation<SimSession, string>({
        query: (saveId) => ({ url: `/saves/${saveId}/simulation/pause`, method: 'POST' }),
        invalidatesTags: (result, _e, saveId) => [
          { type: 'SaveV2', id: saveId },
          { type: 'SimSession', id: `save-${saveId}` },
          ...(result?.id ? [{ type: 'SimSession' as const, id: result.id }] : []),
        ],
      }),

      resumeSaveSimSession: builder.mutation<SimSession, string>({
        query: (saveId) => ({ url: `/saves/${saveId}/simulation/resume`, method: 'POST' }),
        invalidatesTags: (result, _e, saveId) => [
          { type: 'SaveV2', id: saveId },
          { type: 'SimSession', id: `save-${saveId}` },
          ...(result?.id ? [{ type: 'SimSession' as const, id: result.id }] : []),
        ],
      }),

      applyRuntimeReplay: builder.mutation<RuntimeReplayApplyResponse, ApplyRuntimeReplayPayload>({
        query: ({ saveId, config, weatherSeriesId, forceWeatherRebuild, awaitReplay, expectedInputRevision, eventDecisions }) => ({
          url: `/saves/${saveId}/runtime/replay/apply`,
          method: 'POST',
          body: { config, weatherSeriesId, forceWeatherRebuild, awaitReplay, expectedInputRevision, eventDecisions },
        }),
        invalidatesTags: (result, _e, { saveId }) => [
          { type: 'SaveV2', id: saveId },
          { type: 'SaveV2', id: `world-${saveId}` },
          { type: 'SimSession', id: `save-${saveId}` },
          ...(result?.session?.id ? [
            { type: 'SimSession' as const, id: result.session.id },
            { type: 'SimTimeseries' as const, id: `session:${result.session.id}` },
          ] : []),
          'PlantingAttribute' as any,
        ],
      }),

      // --- Weather Series ---

      createWeatherSeries: builder.mutation<WeatherSeries, CreateWeatherSeriesPayload>({
        query: (body) => ({ url: '/simulation/weather', method: 'POST', body }),
        invalidatesTags: ['WeatherSeries'],
      }),

      listWeatherSeries: builder.query<WeatherSeries[], { farmId: string; seriesType?: string }>({
        query: ({ farmId, seriesType }) => {
          const params = new URLSearchParams({ farmId });
          if (seriesType) params.set('seriesType', seriesType);
          return `/simulation/weather?${params}`;
        },
        providesTags: ['WeatherSeries'],
      }),

      getWeatherSeries: builder.query<WeatherSeries, string>({
        query: (id) => `/simulation/weather/${id}`,
        providesTags: (_r, _e, id) => [{ type: 'WeatherSeries', id }],
      }),

      deleteWeatherSeries: builder.mutation<{ message: string }, string>({
        query: (id) => ({ url: `/simulation/weather/${id}`, method: 'DELETE' }),
        invalidatesTags: ['WeatherSeries'],
      }),

      addWeatherDataPoints: builder.mutation<WeatherDataPoint[], { seriesId: string } & AddWeatherDataPointsPayload>({
        query: ({ seriesId, points }) => ({
          url: `/simulation/weather/${seriesId}/data`,
          method: 'POST',
          body: { points },
        }),
        invalidatesTags: (_r, _e, { seriesId }) => [{ type: 'WeatherSeries', id: seriesId }],
      }),

      cloneWeatherSeries: builder.mutation<WeatherSeries, { sourceSeriesId: string } & CloneWeatherSeriesPayload>({
        query: ({ sourceSeriesId, ...body }) => ({
          url: `/simulation/weather/${sourceSeriesId}/clone`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['WeatherSeries'],
      }),

      getWeatherDataPoints: builder.query<WeatherDataPoint[], { seriesId: string; from?: string; to?: string; limit?: number }>({
        query: ({ seriesId, from, to, limit }) => {
          const params = new URLSearchParams();
          if (from) params.set('from', from);
          if (to) params.set('to', to);
          if (limit) params.set('limit', String(limit));
          return `/simulation/weather/${seriesId}/data?${params}`;
        },
        providesTags: (_r, _e, { seriesId }) => [{ type: 'WeatherSeries', id: seriesId }],
      }),

      // --- Weather Maps (hot/cold storage) ---

      listWeatherMaps: builder.query<WeatherMapItem[], string>({
        query: (farmId) => `/simulation/weather/maps?farmId=${farmId}`,
        providesTags: ['WeatherSeries'],
      }),

      saveWeatherSnapshot: builder.mutation<{ id: string; savedPoints: number }, string>({
        query: (seriesId) => ({ url: `/simulation/weather/${seriesId}/save`, method: 'POST' }),
        invalidatesTags: ['WeatherSeries'],
      }),

      activateWeatherMap: builder.mutation<WeatherSeries, { seriesId: string; farmId: string }>({
        query: ({ seriesId, farmId }) => ({
          url: `/simulation/weather/${seriesId}/activate`,
          method: 'POST',
          body: { farmId },
        }),
        invalidatesTags: ['WeatherSeries'],
      }),

      saveAsWeatherMap: builder.mutation<WeatherSeries, { farmId: string; name: string; sourceSeriesId?: string }>({
        query: (body) => ({ url: '/simulation/weather/save-as', method: 'POST', body }),
        invalidatesTags: ['WeatherSeries'],
      }),

      getWeatherImportLocations: builder.query<WeatherImportLocationsResponse, void>({
        query: () => '/simulation/weather/import/locations',
      }),

      importWeatherSeries: builder.mutation<WeatherImportResult, WeatherImportRequest>({
        query: (body) => ({ url: '/simulation/weather/import', method: 'POST', body }),
        invalidatesTags: ['WeatherSeries'],
      }),

      extendWeatherSeries: builder.mutation<WeatherExtendResult, WeatherExtendRequest>({
        query: ({ seriesId, ...body }) => ({
          url: `/simulation/weather/${seriesId}/extend`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_r, _e, { seriesId }) => [
          'WeatherSeries',
          'SimTimeseries',
          { type: 'WeatherSeries', id: seriesId },
        ],
      }),

      getWeatherYearPolicy: builder.query<WeatherYearPolicy, string>({
        query: (seriesId) => `/simulation/weather/${seriesId}/year-policy`,
        providesTags: (_r, _e, seriesId) => [{ type: 'WeatherSeries', id: seriesId }],
      }),

      updateWeatherYearPolicy: builder.mutation<
        WeatherYearPolicy,
        { seriesId: string; mode: WeatherYearPolicyMode; defaultSelection?: WeatherYearSelection }
      >({
        query: ({ seriesId, ...body }) => ({
          url: `/simulation/weather/${seriesId}/year-policy`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: (_r, _e, { seriesId }) => [{ type: 'WeatherSeries', id: seriesId }],
      }),

      materializeWeatherYear: builder.mutation<
        WeatherYearAssignment & { alreadyMaterialized?: boolean },
        { seriesId: string; targetYear: number; selection: WeatherYearSelection }
      >({
        query: ({ seriesId, targetYear, selection }) => ({
          url: `/simulation/weather/${seriesId}/years/${targetYear}/materialize`,
          method: 'POST',
          body: { selection },
        }),
        invalidatesTags: (_r, _e, { seriesId }) => [
          { type: 'WeatherSeries', id: seriesId },
          'SimTimeseries',
        ],
      }),

      // --- Growth Profiles ---

      listGrowthProfiles: builder.query<GrowthProfile[], { speciesId?: number; cultivarId?: number }>({
        query: ({ speciesId, cultivarId } = {}) => {
          const params = new URLSearchParams();
          if (speciesId) params.set('speciesId', String(speciesId));
          if (cultivarId) params.set('cultivarId', String(cultivarId));
          return `/simulation/growth-profiles?${params}`;
        },
        providesTags: ['GrowthProfile'],
      }),

      getGrowthProfile: builder.query<GrowthProfile, string>({
        query: (id) => `/simulation/growth-profiles/${id}`,
        providesTags: (_r, _e, id) => [{ type: 'GrowthProfile', id }],
      }),

      createGrowthProfile: builder.mutation<GrowthProfile, Partial<GrowthProfile>>({
        query: (body) => ({ url: '/simulation/growth-profiles', method: 'POST', body }),
        invalidatesTags: ['GrowthProfile'],
      }),

      updateGrowthProfile: builder.mutation<GrowthProfile, { id: string; body: Partial<GrowthProfile> }>({
        query: ({ id, body }) => ({ url: `/simulation/growth-profiles/${id}`, method: 'PATCH', body }),
        invalidatesTags: (_r, _e, { id }) => [{ type: 'GrowthProfile', id }],
      }),

      deleteGrowthProfile: builder.mutation<{ message: string }, string>({
        query: (id) => ({ url: `/simulation/growth-profiles/${id}`, method: 'DELETE' }),
        invalidatesTags: ['GrowthProfile'],
      }),

      // --- Climate Norms ---

      getClimateNorms: builder.query<ClimateNorm[], string>({
        query: (farmId) => `/simulation/climate-norms?farmId=${farmId}`,
        providesTags: (_r, _e, farmId) => [{ type: 'ClimateNorm', id: farmId }],
      }),

      upsertClimateNorms: builder.mutation<ClimateNorm[], { farmId: string; norms: Partial<ClimateNorm>[] }>({
        query: ({ farmId, norms }) => ({
          url: '/simulation/climate-norms',
          method: 'PUT',
          body: { farmId, norms },
        }),
        invalidatesTags: (_r, _e, { farmId }) => [{ type: 'ClimateNorm', id: farmId }],
      }),

      getDailyClimateNorms: builder.query<ClimateNormDaily[], string>({
        query: (farmId) => `/simulation/climate-norms/daily?farmId=${farmId}`,
        providesTags: (_r, _e, farmId) => [{ type: 'ClimateNormDaily', id: farmId }],
      }),

      upsertDailyClimateNorms: builder.mutation<ClimateNormDaily[], { farmId: string; norms: Partial<ClimateNormDaily>[] }>({
        query: ({ farmId, norms }) => ({
          url: '/simulation/climate-norms/daily',
          method: 'PUT',
          body: { farmId, norms },
        }),
        invalidatesTags: (_r, _e, { farmId }) => [{ type: 'ClimateNormDaily', id: farmId }],
      }),

      getSimulationTimeseries: builder.query<SimulationTimeseriesResponse, {
        sessionId: string;
        plantingId: string;
        from?: string;
        to?: string;
        properties?: string[];
      }>({
        query: ({ sessionId, plantingId, from, to, properties }) => {
          const params = new URLSearchParams({ plantingId });
          if (from) params.set('from', from);
          if (to) params.set('to', to);
          if (properties?.length) params.set('properties', properties.join(','));
          return `/simulation/sessions/${sessionId}/timeseries?${params.toString()}`;
        },
        providesTags: (_r, _e, { sessionId, plantingId }) => [
          { type: 'SimTimeseries', id: `session:${sessionId}` },
          { type: 'SimTimeseries', id: `session:${sessionId}:planting:${plantingId}` },
        ],
        async onQueryStarted({ plantingId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(apiSlice.util.invalidateTags([
              { type: 'PlantingAttribute', id: plantingId },
            ]));
          } catch {
            // no-op: keep previous cache on fetch failure
          }
        },
      }),

      getSimulationSessionWeather: builder.query<SimulationSessionWeatherResponse, {
        sessionId: string;
        from?: string;
        to?: string;
      }>({
        query: ({ sessionId, from, to }) => {
          const params = new URLSearchParams();
          if (from) params.set('from', from);
          if (to) params.set('to', to);
          const query = params.toString();
          return `/simulation/sessions/${sessionId}/weather${query ? `?${query}` : ''}`;
        },
        providesTags: (_r, _e, { sessionId }) => [
          { type: 'SimTimeseries', id: `session:${sessionId}` },
        ],
      }),

      // --- Greenhouse Climate ---
      listGreenhouseClimatePeriods: builder.query<GreenhouseClimateListResponse, {
        sessionId: string;
        structuralObjectId?: string;
      }>({
        query: ({ sessionId, structuralObjectId }) => {
          const params = new URLSearchParams();
          if (structuralObjectId) params.set('structuralObjectId', structuralObjectId);
          const query = params.toString();
          return `/simulation/sessions/${sessionId}/greenhouse-climate${query ? `?${query}` : ''}`;
        },
        transformResponse: (response: { success: boolean; data: GreenhouseClimateListResponse }) => response.data,
        providesTags: (_r, _e, { sessionId, structuralObjectId }) => [
          { type: 'GreenhouseClimate', id: `session:${sessionId}` },
          ...(structuralObjectId ? [{ type: 'GreenhouseClimate' as const, id: `session:${sessionId}:so:${structuralObjectId}` }] : []),
        ],
      }),

      createGreenhouseClimatePeriod: builder.mutation<GreenhouseClimatePeriod, {
        sessionId: string;
        payload: UpsertGreenhouseClimatePeriodPayload;
      }>({
        query: ({ sessionId, payload }) => ({
          url: `/simulation/sessions/${sessionId}/greenhouse-climate`,
          method: 'POST',
          body: payload,
        }),
        transformResponse: (response: { success: boolean; data: GreenhouseClimatePeriod }) => response.data,
        invalidatesTags: (_r, _e, { sessionId, payload }) => [
          { type: 'GreenhouseClimate', id: `session:${sessionId}` },
          ...(payload.structuralObjectId ? [{ type: 'GreenhouseClimate' as const, id: `session:${sessionId}:so:${payload.structuralObjectId}` }] : []),
          { type: 'SimSession', id: sessionId },
          { type: 'SimTimeseries', id: `session:${sessionId}` },
        ],
      }),

      updateGreenhouseClimatePeriod: builder.mutation<GreenhouseClimatePeriod, {
        sessionId: string;
        periodId: string;
        structuralObjectId?: string;
        payload: UpsertGreenhouseClimatePeriodPayload;
      }>({
        query: ({ sessionId, periodId, payload }) => ({
          url: `/simulation/sessions/${sessionId}/greenhouse-climate/${periodId}`,
          method: 'PATCH',
          body: payload,
        }),
        transformResponse: (response: { success: boolean; data: GreenhouseClimatePeriod }) => response.data,
        invalidatesTags: (_r, _e, { sessionId, structuralObjectId }) => [
          { type: 'GreenhouseClimate', id: `session:${sessionId}` },
          ...(structuralObjectId ? [{ type: 'GreenhouseClimate' as const, id: `session:${sessionId}:so:${structuralObjectId}` }] : []),
          { type: 'SimSession', id: sessionId },
          { type: 'SimTimeseries', id: `session:${sessionId}` },
        ],
      }),

      deleteGreenhouseClimatePeriod: builder.mutation<void, {
        sessionId: string;
        periodId: string;
        structuralObjectId?: string;
      }>({
        query: ({ sessionId, periodId }) => ({
          url: `/simulation/sessions/${sessionId}/greenhouse-climate/${periodId}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_r, _e, { sessionId, structuralObjectId }) => [
          { type: 'GreenhouseClimate', id: `session:${sessionId}` },
          ...(structuralObjectId ? [{ type: 'GreenhouseClimate' as const, id: `session:${sessionId}:so:${structuralObjectId}` }] : []),
          { type: 'SimSession', id: sessionId },
          { type: 'SimTimeseries', id: `session:${sessionId}` },
        ],
      }),

      getGreenhouseClimateStatus: builder.query<GreenhouseClimateSnapshot, {
        sessionId: string;
        structuralObjectId: string;
        date?: string;
      }>({
        query: ({ sessionId, structuralObjectId, date }) => {
          const params = new URLSearchParams({ structuralObjectId });
          if (date) params.set('date', date);
          return `/simulation/sessions/${sessionId}/greenhouse-climate/status?${params.toString()}`;
        },
        transformResponse: (response: { success: boolean; data: GreenhouseClimateSnapshot }) => response.data,
        providesTags: (_r, _e, { sessionId, structuralObjectId }) => [
          { type: 'GreenhouseClimate', id: `session:${sessionId}` },
          { type: 'GreenhouseClimate', id: `session:${sessionId}:so:${structuralObjectId}` },
        ],
      }),

      getGreenhouseClimateTimeseries: builder.query<GreenhouseClimateTimeseriesResponse, {
        sessionId: string;
        structuralObjectId: string;
        from?: string;
        to?: string;
      }>({
        query: ({ sessionId, structuralObjectId, from, to }) => {
          const params = new URLSearchParams({ structuralObjectId });
          if (from) params.set('from', from);
          if (to) params.set('to', to);
          return `/simulation/sessions/${sessionId}/greenhouse-climate/timeseries?${params.toString()}`;
        },
        transformResponse: (response: { success: boolean; data: GreenhouseClimateTimeseriesResponse }) => response.data,
        providesTags: (_r, _e, { sessionId, structuralObjectId }) => [
          { type: 'GreenhouseClimate', id: `session:${sessionId}` },
          { type: 'GreenhouseClimate', id: `session:${sessionId}:so:${structuralObjectId}` },
          { type: 'SimTimeseries', id: `session:${sessionId}` },
        ],
      }),

      // --- Save-scoped events (replaces legacy /api/simulation/sessions/:id/events) ---
      listSimEvents: builder.query<{ events: SimEvent[] }, {
        saveId: string;
        sessionId?: string;
        plantingId?: string;
        bedId?: string;
        structuralObjectId?: string;
        from?: string;
        to?: string;
      }>({
        query: ({ saveId, plantingId, bedId, structuralObjectId, from, to }) => {
          const params = new URLSearchParams();
          if (plantingId) params.set('plantingId', plantingId);
          if (bedId) params.set('bedId', bedId);
          if (structuralObjectId) params.set('structuralObjectId', structuralObjectId);
          if (from) params.set('from', from);
          if (to) params.set('to', to);
          const qs = params.toString();
          return `/saves/${saveId}/events${qs ? `?${qs}` : ''}`;
        },
        providesTags: (_r, _e, { saveId, sessionId }) => [
          { type: 'SimEvent', id: `save:${saveId}` },
          ...(sessionId ? [{ type: 'SimEvent' as const, id: sessionId }] : []),
        ],
      }),

      createSimEvent: builder.mutation<SimEvent, CreateSimEventPayload>({
        query: ({ saveId, sessionId: _omitSessionId, ...body }) => ({
          url: `/saves/${saveId}/events`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_r, _e, { saveId, sessionId, plantingId, structuralObjectId }) => [
          { type: 'SimEvent', id: `save:${saveId}` },
          { type: 'SaveV2' as const, id: saveId },
          { type: 'SaveV2' as const, id: `world-${saveId}` },
          { type: 'SaveV2' as const, id: `comparison-${saveId}` },
          ...(plantingId
            ? [
                { type: 'Planting' as const, id: plantingId },
                { type: 'PlantingAttribute' as const, id: plantingId },
                { type: 'PlantingEvent' as const, id: plantingId },
              ]
            : []),
          ...(sessionId
            ? [
                { type: 'SimEvent' as const, id: sessionId },
                { type: 'SimTimeseries' as const, id: `session:${sessionId}` },
                { type: 'GreenhouseClimate' as const, id: `session:${sessionId}` },
              ]
            : []),
          ...(sessionId && structuralObjectId
            ? [{ type: 'GreenhouseClimate' as const, id: `session:${sessionId}:so:${structuralObjectId}` }]
            : []),
          ...(sessionId && plantingId
            ? [{ type: 'SimTimeseries' as const, id: `session:${sessionId}:planting:${plantingId}` }]
            : []),
        ],
      }),

      deleteSimEvent: builder.mutation<void, {
        saveId: string;
        sessionId?: string;
        eventId: string;
        structuralObjectId?: string;
        expected_save_revision?: number | string;
      }>({
        query: ({ saveId, eventId, expected_save_revision }) => ({
          url: `/saves/${saveId}/events/${eventId}`,
          method: 'DELETE',
          body: expected_save_revision != null ? { expected_save_revision } : undefined,
        }),
        invalidatesTags: (_r, _e, { saveId, sessionId, structuralObjectId }) => [
          { type: 'SimEvent', id: `save:${saveId}` },
          { type: 'SaveV2' as const, id: saveId },
          { type: 'SaveV2' as const, id: `world-${saveId}` },
          { type: 'SaveV2' as const, id: `comparison-${saveId}` },
          ...(sessionId
            ? [
                { type: 'SimEvent' as const, id: sessionId },
                { type: 'SimTimeseries' as const, id: `session:${sessionId}` },
                { type: 'GreenhouseClimate' as const, id: `session:${sessionId}` },
              ]
            : []),
          ...(sessionId && structuralObjectId
            ? [{ type: 'GreenhouseClimate' as const, id: `session:${sessionId}:so:${structuralObjectId}` }]
            : []),
        ],
      }),

      getSimOverview: builder.query<DayOverview, string>({
        query: (sessionId) => `/simulation/sessions/${sessionId}/overview`,
        providesTags: (_r, _e, sessionId) => [{ type: 'SimOverview', id: sessionId }],
      }),

      getEntityTimeseries: builder.query<EntityTimeseriesResponse<SimulationTimeseriesEntry>, {
        sessionId: string;
        entityType: EntityTimeseriesType;
        entityId: string;
        properties?: string[];
        fromDate?: string;
        toDate?: string;
      }>({
        query: ({ sessionId, entityType, entityId, properties, fromDate, toDate }) => {
          const params = new URLSearchParams({ entityType, entityId });
          if (properties?.length) params.set('properties', properties.join(','));
          if (fromDate) params.set('fromDate', fromDate);
          if (toDate) params.set('toDate', toDate);
          return `/simulation/sessions/${sessionId}/entity-timeseries?${params.toString()}`;
        },
        providesTags: (_r, _e, { sessionId, entityType, entityId }) => [
          { type: 'SimTimeseries', id: `session:${sessionId}` },
          { type: 'SimTimeseries', id: `session:${sessionId}:${entityType}:${entityId}` },
        ],
      }),

      // --- Real Day ---
      triggerRealTick: builder.mutation<RealDayResult, { farmId: string; timezone?: string }>({
        query: (body) => ({ url: '/simulation/real-tick', method: 'POST', body }),
        invalidatesTags: ['SimTimeseries'],
      }),

      listRealTickLog: builder.query<RealDayLogEntry[], { farmId: string; limit?: number }>({
        query: ({ farmId, limit }) => {
          const params = new URLSearchParams({ farmId });
          if (limit) params.set('limit', String(limit));
          return `/simulation/real-tick-log?${params.toString()}`;
        },
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetSimSessionQuery,
  useGetActiveSimSessionQuery,
  useListSimSessionsQuery,
  useCancelSimSessionMutation,
  usePauseSimSessionMutation,
  useResumeSimSessionMutation,
  useAdvanceSimDaysMutation,
  useAdvanceSaveSimDaysMutation,
  useResetSimSessionMutation,
  useResetSaveSimSessionMutation,
  usePauseSaveSimSessionMutation,
  useResumeSaveSimSessionMutation,
  useApplyRuntimeReplayMutation,
  useCreateWeatherSeriesMutation,
  useListWeatherSeriesQuery,
  useGetWeatherSeriesQuery,
  useDeleteWeatherSeriesMutation,
  useAddWeatherDataPointsMutation,
  useCloneWeatherSeriesMutation,
  useGetWeatherDataPointsQuery,
  useListWeatherMapsQuery,
  useSaveWeatherSnapshotMutation,
  useActivateWeatherMapMutation,
  useSaveAsWeatherMapMutation,
  useGetWeatherImportLocationsQuery,
  useImportWeatherSeriesMutation,
  useExtendWeatherSeriesMutation,
  useGetWeatherYearPolicyQuery,
  useUpdateWeatherYearPolicyMutation,
  useMaterializeWeatherYearMutation,
  useListGrowthProfilesQuery,
  useGetGrowthProfileQuery,
  useCreateGrowthProfileMutation,
  useUpdateGrowthProfileMutation,
  useDeleteGrowthProfileMutation,
  useGetClimateNormsQuery,
  useUpsertClimateNormsMutation,
  useGetDailyClimateNormsQuery,
  useUpsertDailyClimateNormsMutation,
  useGetSimulationTimeseriesQuery,
  useGetSimulationSessionWeatherQuery,
  useListGreenhouseClimatePeriodsQuery,
  useCreateGreenhouseClimatePeriodMutation,
  useUpdateGreenhouseClimatePeriodMutation,
  useDeleteGreenhouseClimatePeriodMutation,
  useGetGreenhouseClimateStatusQuery,
  useGetGreenhouseClimateTimeseriesQuery,
  useListSimEventsQuery,
  useCreateSimEventMutation,
  useDeleteSimEventMutation,
  useGetSimOverviewQuery,
  useGetEntityTimeseriesQuery,
  useTriggerRealTickMutation,
  useListRealTickLogQuery,
} = simulationApi;

export const useGetSaveRuntimeSessionQuery = (
  saveIdArg: SaveRuntimeSessionQueryArg,
  options?: SaveRuntimeSessionQueryOptions
) => {
  const world = useWorldOptional();
  const refreshWorld = useRefreshWorldOptional();
  const saveId = typeof saveIdArg === 'string' ? saveIdArg : null;
  const skipped = Boolean(options?.skip || saveIdArg === skipToken || !saveId);

  const data = useMemo<SimSession | null | undefined>(() => {
    if (skipped) return undefined;
    if (!world || world.saveId !== saveId) return undefined;
    return buildSaveRuntimeSession(world);
  }, [saveId, skipped, world]);

  const refetch = useCallback(async () => {
    if (!skipped && refreshWorld) {
      await refreshWorld();
    }
    return { data };
  }, [data, refreshWorld, skipped]);

  return {
    data,
    currentData: data,
    isLoading: !skipped && (!world || world.saveId !== saveId),
    isFetching: false,
    isError: false,
    error: undefined,
    refetch,
  };
};

export default simulationApi;
